import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';
import {
  platformService,
  type InvitationCodeInput,
  type PlatformInvitationCode,
  type PlatformPlanOption,
} from '@/services/api/platform.service';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { PlatformLayout } from '@/components/platform/PlatformLayout';
import { InvitationCodeFormDialog } from '@/components/platform/InvitationCodeFormDialog';
import { InvitationCodeStatus } from '@/components/platform/InvitationCodeStatus';

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

/**
 * Super-admin list of the invitation codes used by the invite-only landing
 * pages: create/edit, toggle, re-check against Stripe, open the signups.
 */
export default function PlatformInvitationCodesPage() {
  const [, setLocation] = useLocation();
  const [codes, setCodes] = useState<PlatformInvitationCode[]>([]);
  const [plans, setPlans] = useState<PlatformPlanOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PlatformInvitationCode | null>(null);

  const handleError = useCallback((err: any, fallback: string) => {
    // No/invalid platform session → back to platform login.
    if (err?.response?.status === 401) {
      setLocation('/platform/login');
      return;
    }
    setError(err?.response?.data?.error || fallback);
  }, [setLocation]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await platformService.listInvitationCodes();
      setCodes(result.codes);
      setPlans(result.plans);
    } catch (err) {
      handleError(err, 'Could not load invitation codes');
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    load();
  }, [load]);

  const replace = (updated: PlatformInvitationCode) =>
    setCodes((current) => current.map((c) => (c.id === updated.id ? updated : c)));

  const runOnCode = async (id: number, action: () => Promise<PlatformInvitationCode>, fallback: string) => {
    setBusyId(id);
    setError(null);
    try {
      replace(await action());
    } catch (err) {
      handleError(err, fallback);
    } finally {
      setBusyId(null);
    }
  };

  const handleSubmit = async (input: InvitationCodeInput) => {
    if (editing) {
      replace(await platformService.updateInvitationCode(editing.id, input));
    } else {
      const created = await platformService.createInvitationCode(input);
      setCodes((current) => [created, ...current]);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (code: PlatformInvitationCode) => {
    setEditing(code);
    setDialogOpen(true);
  };

  return (
    <PlatformLayout
      title="Invitation codes"
      wide
      actions={<Button variant="primary" onClick={openCreate}>New code</Button>}
    >
      {error && <div role="alert" className="mb-4 rounded bg-danger-soft px-3 py-2 text-sm text-danger-text">{error}</div>}

      <div className="overflow-x-auto rounded-2xl border border-line bg-surface shadow-card">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-left text-ink-subtle">
            <tr>
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">Plan</th>
              <th className="px-3 py-2">Stripe coupon</th>
              <th className="px-3 py-2">Active</th>
              <th className="px-3 py-2">Expires</th>
              <th className="px-3 py-2">Signups</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {codes.map((code) => (
              <tr key={code.id} className="border-t border-line align-top">
                <td className="px-3 py-2 font-medium">
                  <Link href={`/platform/invitation-codes/${code.id}`} className="text-brand-700 hover:underline">
                    {code.code}
                  </Link>
                </td>
                <td className="px-3 py-2">{code.planTarget ?? '—'}</td>
                <td className="px-3 py-2 font-mono text-xs">{code.stripeCodeId}</td>
                <td className="px-3 py-2">
                  <Switch
                    checked={code.active}
                    disabled={busyId === code.id}
                    aria-label={`${code.code} active`}
                    onCheckedChange={(active) =>
                      runOnCode(code.id, () => platformService.updateInvitationCode(code.id, { active }), 'Could not update the code')
                    }
                  />
                </td>
                <td className="px-3 py-2">{formatDate(code.expiresAt)}</td>
                <td className="px-3 py-2" title="Granted / signed up (max)">
                  {code.grantedCount} / {code.signupsCount}
                  {code.maxSignups ? <span className="text-ink-subtle"> (max {code.maxSignups})</span> : null}
                </td>
                <td className="px-3 py-2"><InvitationCodeStatus code={code} /></td>
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      disabled={busyId === code.id}
                      onClick={() => runOnCode(code.id, () => platformService.checkInvitationCode(code.id), 'Check failed')}
                    >
                      {busyId === code.id ? 'Checking…' : 'Re-check'}
                    </Button>
                    <Button variant="outline" onClick={() => openEdit(code)}>Edit</Button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && codes.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-ink-subtle">No invitation codes yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <InvitationCodeFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        plans={plans}
        initial={editing}
        onSubmit={handleSubmit}
      />
    </PlatformLayout>
  );
}
